import { DateType } from '../TimeSelector';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { keepTwoOrLine, keepTwoAndWanOrLine, formatNumberWithUnit } from '@/utils/utils';

// 时段颜色配置
export const TIME_PERIOD_COLORS = {
  sharp: '#FF6D2C', // 尖
  peak: '#CE90D1', // 峰
  flat: '#0DB6D9', // 平
  valley: '#63BC7F', // 谷
};

// 标记点图标 - 带箭头的矩形框
const MARK_ICONS = {
  max: 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+Cjxzdmcgd2lkdGg9IjYycHgiIGhlaWdodD0iMzdweCIgdmlld0JveD0iMCAwIDYyIDM3IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPkdyb3VwPC90aXRsZT4KICAgIDxkZXNjPkNyZWF0ZWQgd2l0aCBTa2V0Y2guPC9kZXNjPgogICAgPGRlZnM+PC9kZWZzPgogICAgPGcgaWQ9IlBhZ2UtMSIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgaWQ9Ikdyb3VwIiBmaWxsPSIjRkYwMDAwIj4KICAgICAgICAgICAgPHJlY3QgaWQ9IlJlY3RhbmdsZSIgeD0iMCIgeT0iMCIgd2lkdGg9IjYyIiBoZWlnaHQ9IjI2Ij48L3JlY3Q+CiAgICAgICAgICAgIDxwb2x5Z29uIGlkPSJUcmlhbmdsZSIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjkuNTAwMDAwLCAzMS4wMDAwMDApIHNjYWxlKDEsIC0xKSB0cmFuc2xhdGUoLTI5LjUwMDAwMCwgLTMxLjAwMDAwMCkgIiBwb2ludHM9IjI5LjUgMjUgNDAgMzcgMTkgMzciPjwvcG9seWdvbj4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==',
  min: 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+Cjxzdmcgd2lkdGg9IjYycHgiIGhlaWdodD0iMzdweCIgdmlld0JveD0iMCAwIDYyIDM3IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPkdyb3VwPC90aXRsZT4KICAgIDxkZXNjPkNyZWF0ZWQgd2l0aCBTa2V0Y2guPC9kZXNjPgogICAgPGRlZnM+PC9kZWZzPgogICAgPGcgaWQ9IlBhZ2UtMSIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgaWQ9Ikdyb3VwIiBmaWxsPSIjMDBBREZGIj4KICAgICAgICAgICAgPHJlY3QgaWQ9IlJlY3RhbmdsZSIgeD0iMCIgeT0iMCIgd2lkdGg9IjYyIiBoZWlnaHQ9IjI2Ij48L3JlY3Q+CiAgICAgICAgICAgIDxwb2x5Z29uIGlkPSJUcmlhbmdsZSIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjkuNTAwMDAwLCAzMS4wMDAwMDApIHNjYWxlKDEsIC0xKSB0cmFuc2xhdGUoLTI5LjUwMDAwMCwgLTMxLjAwMDAwMCkgIiBwb2ludHM9IjI5LjUgMjUgNDAgMzcgMTkgMzciPjwvcG9seWdvbj4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==',
};

// 获取X轴格式
export const getXAxisFormat = (timeResolution: DateType) => {
  switch (timeResolution) {
    case DateType.YEAR:
      return 'YYYY-MM';
    case DateType.MONTH:
    case DateType.CUSTOM:
    default:
      return 'MM-DD';
  }
};

export interface ChartDataProps {
  sharpDataList?: { time?: number; value?: number; total?: number; rate?: number }[];
  peakDataList?: { time?: number; value?: number; total?: number; rate?: number }[];
  flatDataList?: { time?: number; value?: number; total?: number; rate?: number }[];
  valleyDataList?: { time?: number; value?: number; total?: number; rate?: number }[];
  costDataList?: { time?: number; value?: number }[];
}

export const getChartOption = (
  data: ChartDataProps,
  timeResolution: DateType,
  unit: string = 'kWh',
  maxValue?: number | null,
  minValue?: number | null,
  avgValue?: number | null
) => {
  const xFormat = getXAxisFormat(timeResolution);

  return {
    grid: {
      top: 60,
      bottom: 50,
      containLabel: true,
    },
    legend: [
      {
        right: 160,
        top: 10,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
        data: [
          { name: '尖时段', icon: 'rect' },
          { name: '峰时段', icon: 'rect' },
          { name: '平时段', icon: 'rect' },
          { name: '谷时段', icon: 'rect' },
        ],
      },
      {
        top: 15,
        right: 70,
        icon: 'rect',
        itemWidth: 16,
        itemHeight: 4,
        itemGap: 16,
        padding: [0, 8, 0, 0],
        data: [{ name: '总用能成本' }],
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      extraCssText: 'min-width: 180px;',
      formatter: function (params: any) {
        // 使用第一个参数的data[0]作为时间戳
        const time = params?.[0]?.data?.[0];
        const dateStr = time ? dayjs(time).format('YYYY-MM-DD') : '--';

        // 使用第一个参数的data[3]作为总用能值
        const totalValue = params?.[0]?.data?.[3];

        // 从总用能成本系列中获取成本值
        const costItem = params?.find((item: any) => item.seriesName === '总用能成本');
        const costValue = costItem?.data?.[1];

        let content = `<div style="font-size:12px;line-height:20px;">
          <div style="font-weight:bold;margin-bottom:8px;">${dateStr}</div>
          <div style="display:flex;justify-content:space-between;margin-bottom:8px;">
            <span>总用能成本</span>
            <span>${keepTwoAndWanOrLine(costValue)}元</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:8px;">
            <span>总用能</span>
            <span>${formatNumberWithUnit(totalValue, unit)}</span>
          </div>`;

        // 如果提供了平均值，在提示框中显示

        content += `<div style="display:flex;justify-content:space-between;margin-bottom:8px;">
            <span>平均值</span>
            <span>${formatNumberWithUnit(avgValue ?? undefined, unit)}</span>
          </div>`;

        // 只显示尖峰平谷时段，排除总用能成本
        params
          .filter((item: any) => item.seriesName !== '总用能成本')
          .forEach((item: any) => {
            const value = item?.data?.[1];
            const rate = item?.data?.[2];
            content += `<div style="display:flex;justify-content:space-between;margin-bottom:8px;">
            <span>${item.marker}${item.seriesName}</span>
            <span style="margin-left:12px;">${formatNumberWithUnit(
              value,
              unit
            )}<span style="color:#ffcb47;margin-left:6px;">(${keepTwoOrLine(rate)}%)</span></span>
          </div>`;
          });

        content += `</div>`;

        return content;
      },
    },
    toolbox: {
      width: 86,
      height: 100,
      top: 4,
      right: 10,
      itemSize: 20,
      feature: {
        brush: {
          type: ['lineX', 'clear'],
          title: {
            lineX: '横向选择',
            clear: '清除',
          },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    xAxis: {
      type: 'time',
      axisTick: { alignWithLabel: true },
      minInterval: 60 * 1000 * 60 * 24,
      axisLabel: {
        formatter: (value: number) => dayjs(value).format(xFormat),
        margin: 8,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: unit,
        nameGap: 8,
      },
      {
        type: 'value',
        name: '元',
        nameGap: 8,
        splitLine: { show: false },
      },
    ],
    dataZoom: [
      {
        show: true,
        type: 'slider',
        labelFormatter: (value: string) => dayjs(value).format(xFormat),
        bottom: 20,
      },
    ],
    series: [
      {
        name: '尖时段',
        type: 'bar',
        stack: 'total',
        barMaxWidth: 40,
        data: data.sharpDataList?.map(a => [a.time, a.value, a.rate, a.total]),
        itemStyle: { color: TIME_PERIOD_COLORS.sharp },
      },
      {
        name: '峰时段',
        type: 'bar',
        stack: 'total',
        data: data.peakDataList?.map(a => [a.time, a.value, a.rate, a.total]),
        itemStyle: { color: TIME_PERIOD_COLORS.peak },
      },
      {
        name: '平时段',
        type: 'bar',
        stack: 'total',
        data: data.flatDataList?.map(a => [a.time, a.value, a.rate, a.total]),
        itemStyle: { color: TIME_PERIOD_COLORS.flat },
      },
      {
        name: '谷时段',
        type: 'bar',
        stack: 'total',
        data: data.valleyDataList?.map(a => [a.time, a.value, a.rate, a.total]),
        itemStyle: { color: TIME_PERIOD_COLORS.valley },
        // 如果提供了平均值，显示平均线
        ...(avgValue != null
          ? {
              markLine: {
                silent: true,
                symbol: ['none', 'none'],
                data: [
                  {
                    label: {
                      formatter: () => `平均值：${formatNumberWithUnit(avgValue, unit)}`,
                      position: 'insideEndTop',
                      color: '#63BC7F',
                    },
                    yAxis: avgValue,
                    lineStyle: {
                      type: 'dashed',
                      width: 2,
                      color: '#63BC7F',
                    },
                  },
                ],
              },
            }
          : {}),
        // 如果提供了最大值或最小值，显示标记点
        ...(maxValue != null || minValue != null
          ? {
              markPoint: {
                symbolSize: [130, 30],
                symbolOffset: [0, -13],
                label: {
                  show: true,
                  offset: [0, -4],
                  color: '#FFFFFF',
                },
                emphasis: {
                  show: true,
                  offset: [0, -4],
                },
                data: [
                  ...(maxValue != null
                    ? [
                        {
                          type: 'max',
                          name: '最高',
                          symbol: `image://${MARK_ICONS.max}`,
                          value: maxValue,
                          label: {
                            formatter: () => `最大值：${formatNumberWithUnit(maxValue, unit)}`,
                          },
                        },
                      ]
                    : []),
                  ...(minValue != null
                    ? [
                        {
                          type: 'min',
                          name: '最低',
                          symbol: `image://${MARK_ICONS.min}`,
                          value: minValue,
                          label: {
                            formatter: () => `最小值：${formatNumberWithUnit(minValue, unit)}`,
                          },
                        },
                      ]
                    : []),
                ],
              },
            }
          : {}),
      },
      {
        name: '总用能成本',
        type: 'line',
        yAxisIndex: 1,
        data: data.costDataList?.map(a => [a.time, a.value]),
        itemStyle: { color: '#FFCB47' },
        lineStyle: { width: 2 },
        symbol: 'circle',
        symbolSize: 6,
      },
    ],
  };
};
